import { useApp, API } from "@/App";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";

// Default placeholder for clubs without logos
const ClubLogo = ({ logoPath, clubName }) => {
  if (logoPath) {
    return (
      <img 
        src={`${API}/images/${logoPath.replace('ketley-league/', '')}`}
        alt={clubName}
        className="w-8 h-8 rounded-md object-contain shrink-0"
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'flex';
        }}
      />
    );
  }
  
  // Default placeholder with initials
  const initials = clubName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
      <span className="text-xs font-bold text-primary">{initials}</span>
    </div>
  );
};

const LeagueTable = ({ standings, loading, onClubClick }) => {
  const navigate = useNavigate();
  
  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  if (!standings || standings.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground" data-testid="no-standings">
        <p className="text-lg">No standings available</p>
        <p className="text-sm mt-1">Results will appear once matches are played</p>
      </div>
    );
  }

  const handleClubClick = (clubId, clubName) => {
    navigate(`/fixtures?club=${clubId}`);
  };

  return (
    <div className="bg-white rounded-lg border border-border overflow-hidden" data-testid="league-table">
      <Table>
        <TableHeader>
          <TableRow className="bg-primary text-primary-foreground hover:bg-primary">
            <TableHead className="text-primary-foreground w-12 text-center"></TableHead>
            <TableHead className="text-primary-foreground">Club</TableHead>
            <TableHead className="text-primary-foreground text-center w-8">P</TableHead>
            <TableHead className="text-primary-foreground text-center w-8">W</TableHead>
            <TableHead className="text-primary-foreground text-center w-8">D</TableHead>
            <TableHead className="text-primary-foreground text-center w-8">L</TableHead>
            <TableHead className="text-primary-foreground text-center w-10">+/-</TableHead>
            <TableHead className="text-primary-foreground text-center w-12">Pts</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {standings.map((team, index) => (
            <TableRow 
              key={team.club_id} 
              className="table-row-enter cursor-pointer hover:bg-muted/50 transition-colors"
              style={{ animationDelay: `${index * 50}ms` }}
              data-testid={`standing-row-${index}`}
              onClick={() => handleClubClick(team.club_id, team.club_name)}
            >
              <TableCell className="py-3 px-2" style={{ width: '44px', minWidth: '44px' }}>
                <ClubLogo logoPath={team.club_logo} clubName={team.club_name} />
                {/* Hidden fallback div for image error */}
                <div className="w-8 h-8 rounded-md bg-primary/10 items-center justify-center hidden shrink-0">
                  <span className="text-xs font-bold text-primary">
                    {team.club_name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                  </span>
                </div>
              </TableCell>
              <TableCell className="font-medium text-sm text-primary hover:underline">{team.club_name}</TableCell>
              <TableCell className="text-center text-sm">{team.played}</TableCell>
              <TableCell className="text-center text-green-600 font-medium text-sm">{team.won}</TableCell>
              <TableCell className="text-center text-amber-600 font-medium text-sm">{team.drawn}</TableCell>
              <TableCell className="text-center text-red-500 font-medium text-sm">{team.lost}</TableCell>
              <TableCell className="text-center text-sm">
                <span className={team.points_diff > 0 ? "text-green-600" : team.points_diff < 0 ? "text-red-500" : "text-muted-foreground"}>
                  {team.points_diff > 0 ? "+" : ""}{team.points_diff}
                </span>
              </TableCell>
              <TableCell className="text-center">
                <span className="inline-flex items-center justify-center w-8 h-8 bg-[#CCFF00] text-primary font-bold rounded-md text-sm">
                  {team.points}
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default function HomePage() {
  const { standings, seasons, selectedSeason, setSelectedSeason, loading, adminSettings } = useApp();
  const navigate = useNavigate();

  // Build header image URL
  const headerImageUrl = adminSettings?.header_image_path 
    ? `${API}/images/${adminSettings.header_image_path.replace('ketley-league/', '')}`
    : null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="home-page">
      {/* Title - Centered at top */}
      <div className="bg-primary px-4 pt-6 pb-3">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight text-center" data-testid="league-title">
          {adminSettings?.league_name || "The Ketley League"}
        </h1>
      </div>

      {/* Header Image */}
      {adminSettings?.header_image_path && (
        <div className="w-full bg-primary">
          <img 
            src={headerImageUrl}
            alt={adminSettings?.league_name || "League Header"}
            className="w-full h-auto max-h-48 object-cover"
            data-testid="header-image"
          />
        </div>
      )}

      {/* Season Selector */}
      {seasons.length > 0 && (
        <div className="bg-primary px-4 pb-4 pt-2">
          <Select 
            value={selectedSeason?.id || ""} 
            onValueChange={(id) => setSelectedSeason(seasons.find(s => s.id === id))}
          >
            <SelectTrigger className="w-full max-w-xs bg-white/10 border-white/20 text-white backdrop-blur-sm" data-testid="season-selector">
              <SelectValue placeholder="Select Season" />
            </SelectTrigger>
            <SelectContent>
              {seasons.map((season) => (
                <SelectItem key={season.id} value={season.id}>
                  {season.name} {season.is_active && "(Current)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* League Table */}
      <div className="px-4 -mt-2">
        <LeagueTable standings={standings} loading={loading} />
      </div>

      {/* Legend */}
      <div className="px-4 mt-4 mb-6 pb-24">
        <div className="bg-white rounded-lg border border-border p-3">
          <p className="text-xs text-muted-foreground text-center">
            <span className="font-medium">P</span>=Played &nbsp;
            <span className="font-medium">W</span>=Won &nbsp;
            <span className="font-medium">D</span>=Drawn &nbsp;
            <span className="font-medium">L</span>=Lost &nbsp;
            <span className="font-medium">+/-</span>=Points Diff &nbsp;
            <span className="font-medium">Pts</span>=Points
          </p>
          <p className="text-xs text-muted-foreground text-center mt-1">
            Win=2pts, Draw=1pt | Sorted by Points, then Points Difference
          </p>
        </div>
      </div>
    </div>
  );
}
